import {
    buildOptionalParams,
    buildPath,
    buildTemplatePath,
    Card,
    CrudMode,
    Dialog,
    handleClose,
    Page,
    PageRoute,
    ScreenType,
    useParams,
    useUnsavedChanges,
    useValidationErrors,
} from "@compass/core";
import { FeederSchool, FeederSchoolEditResource, FeederSchoolViewResource, Permission } from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { FeederSchoolPageBody } from "./FeederSchoolPageBody";
import { FeederSchoolPageHeader } from "./FeederSchoolPageHeader";

const routeName = "feederSchool";

class Params {
    id: number = 0;
    crudMode: CrudMode = "view";
    screenType: ScreenType = "standard";
}

export const feederSchoolPageRoute: PageRoute<Params> = {
    handle: "Feeder School",
    templatePath: buildTemplatePath(routeName, Params),
    getPath: (params) => buildPath(routeName, params),
};

export const FeederSchoolPage = () => {
    const ROOT_PERMISSION: Permission = Permission.SystemUtility;
    const navigate = useNavigate();
    const { params } = useParams(routeName, Params);

    const [feederSchool, setFeederSchool] = useState<FeederSchool>({} as FeederSchool);
    const [initFeederSchool, setInitFeederSchool] = useState<FeederSchool>(feederSchool);
    const [viewResource, setViewResource] = useState<FeederSchoolViewResource>({});
    const [editResource, setEditResource] = useState<FeederSchoolEditResource>({});

    const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [feederSchool] });
    const { validationErrors, setValidationErrors } = useValidationErrors();

    useEffect(() => {
        fetch(params.id);
        ApiService.feederSchools.getViewResource().then((response) => {
            setViewResource(response);
        });
    }, []);

    useEffect(() => {
        if (params.crudMode === "edit") {
            ApiService.feederSchools.getEditResource().then((response) => {
                setEditResource(response);
            });
        }
    }, [params.crudMode]);

    const reroute = (id: number, crudMode: CrudMode) =>
        navigate(`../${feederSchoolPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType,  })}`);

    const fetch = (id: number) => {
        ApiService.feederSchools.get(id).then((response) => {
            setFeederSchool(response);
            setInitFeederSchool(response);
            resetUnsavedChanges([response]);
        });
    };

    return (
        <Dialog
            isForm
            open={true}
            onClose={() => handleClose(navigate, unsavedChanges)}
            maxWidth="md"
            header={
                <FeederSchoolPageHeader
                    rootPermission={ROOT_PERMISSION}
                    handle={feederSchoolPageRoute.handle}
                    crudMode={params.crudMode}
                    reroute={reroute}
                    fetch={fetch}
                    feederSchool={feederSchool}
                    setFeederSchool={setFeederSchool}
                    initFeederSchool={initFeederSchool}
                    setValidationErrors={setValidationErrors}
                    unsavedChanges={unsavedChanges}
                    resetUnsavedChanges={resetUnsavedChanges}
                />
            }
            content={
                <FeederSchoolPageBody
                    feederSchool={feederSchool}
                    setFeederSchool={setFeederSchool}
                    viewResource={viewResource}
                    editResource={editResource}
                    crudMode={params.crudMode}
                    validationErrors={validationErrors}
                />
            }
        />
    );
};